import { useState, useEffect } from 'react'
import { Container, Card, Button, Modal, Form, Alert, Badge, ListGroup } from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import networkService from '../services/networkService'

function Networks() {
  const { activeProfile } = useProfile()
  const [networks, setNetworks] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })

  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [loading, setLoading] = useState(false)
  const [subscribingNetworkId, setSubscribingNetworkId] = useState(null)

  useEffect(() => {
    if (activeProfile) {
      loadNetworks()
    }
  }, [activeProfile])

  const loadNetworks = async () => {
    if (!activeProfile) return

    try {
      const data = await networkService.getNetworks()

      // Sort networks: subscribed ones first, then unsubscribed
      const sortedNetworks = data.sort((networkA, networkB) => {
        const aSubscribed = isSubscribed(networkA)
        const bSubscribed = isSubscribed(networkB)
        if (aSubscribed && !bSubscribed) return -1
        if (!aSubscribed && bSubscribed) return 1
        return networkA.name.localeCompare(networkB.name)
      })

      setNetworks(sortedNetworks)
    } catch (err) {
      console.error('Failed to load networks:', err)
    }
  }

  const isSubscribed = (network) => {
    if (!activeProfile) return false
    // Check if active profile is the manager or a member
    if (network.profile_id === activeProfile.id) return true
    if (network.members && network.members.some(member => member.id === activeProfile.id)) return true
    return false
  }

  const isManager = (network) => {
    if (!activeProfile) return false
    return network.profile_id === activeProfile.id
  }

  const handleToggleSubscription = async (network) => {
    if (!activeProfile) return

    setSubscribingNetworkId(network.id)
    setError(null)

    try {
      if (isSubscribed(network)) {
        // Unsubscribe (can't unsubscribe if you're the manager)
        if (isManager(network)) {
          setError('Network managers cannot unsubscribe from their own network')
          return
        }
        await networkService.unsubscribe(network.id, activeProfile.id)
        setSuccess(`Unsubscribed from ${network.name}`)
      } else {
        // Subscribe
        await networkService.subscribe(network.id, activeProfile.id)
        setSuccess(`Subscribed to ${network.name}`)
      }
      loadNetworks()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update subscription')
    } finally {
      setSubscribingNetworkId(null)
    }
  }

  const handleCreateNetwork = async (event) => {
    event.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await networkService.createNetwork({
        name: formData.name,
        description: formData.description,
        profile_id: activeProfile.id
      })

      setSuccess('Network created successfully!')
      setShowCreateModal(false)
      setFormData({ name: '', description: '' })
      loadNetworks()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create network')
    } finally {
      setLoading(false)
    }
  }

  // Separate networks into subscribed and available
  const subscribedNetworks = networks.filter(network => isSubscribed(network))
  const availableNetworks = networks.filter(network => !isSubscribed(network))
  const showNetworkDivider = subscribedNetworks.length > 0 && availableNetworks.length > 0

  return (
    <Container className="mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2>Networks</h2>
          <p className="text-muted mb-0">
            Subscribe to networks to include their members' scores in your search results
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          className="flex-shrink-0"
        >
          Create Network
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <Card.Header>
          <strong>All Networks</strong>
        </Card.Header>
        <ListGroup variant="flush">
          {/* Subscribed Networks */}
          {subscribedNetworks.map((network) => (
            <ListGroup.Item key={network.id} className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id={`network-${network.id}`}
                checked={true}
                onChange={() => handleToggleSubscription(network)}
                disabled={subscribingNetworkId === network.id || isManager(network)}
                className="me-3"
              />
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium">{network.name}</span>
                  {network.verified && (
                    <Badge bg="success" pill>Verified</Badge>
                  )}
                  {isManager(network) && (
                    <Badge bg="primary" pill>Manager</Badge>
                  )}
                </div>
                {network.description && (
                  <small className="text-muted d-block">{network.description}</small>
                )}
              </div>
              <small className="text-muted">
                {network.member_count || 0} members
              </small>
            </ListGroup.Item>
          ))}

          {/* Divider if there are both subscribed and available networks */}
          {showNetworkDivider && (
            <ListGroup.Item className="bg-light py-2">
              <small className="text-muted fw-medium">Available Networks</small>
            </ListGroup.Item>
          )}

          {/* Available (unsubscribed) Networks */}
          {availableNetworks.map((network) => (
            <ListGroup.Item key={network.id} className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id={`network-${network.id}`}
                checked={false}
                onChange={() => handleToggleSubscription(network)}
                disabled={subscribingNetworkId === network.id}
                className="me-3"
              />
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium">{network.name}</span>
                  {network.verified && (
                    <Badge bg="success" pill>Verified</Badge>
                  )}
                </div>
                {network.description && (
                  <small className="text-muted d-block">{network.description}</small>
                )}
              </div>
              <small className="text-muted">
                {network.member_count || 0} members
              </small>
            </ListGroup.Item>
          ))}

          {/* Empty state */}
          {networks.length === 0 && (
            <ListGroup.Item className="text-center py-4">
              <p className="text-muted mb-0">No networks available yet.</p>
            </ListGroup.Item>
          )}
        </ListGroup>
      </Card>

      {/* Create Network Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Network</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateNetwork}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Network Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Science Educators"
                value={formData.name}
                onChange={(event) => setFormData({...formData, name: event.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe the purpose of this network..."
                value={formData.description}
                onChange={(event) => setFormData({...formData, description: event.target.value})}
              />
            </Form.Group>

            <Alert variant="info">
              You will be the manager of this network and can invite other profiles to join.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Network'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}

export default Networks
